import type {EditHistoryDialogQueryQuery} from '@github-ui/server-persisted-queries/types/EditHistoryDialogQuery'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

type DialogNode = NonNullable<EditHistoryDialogQueryQuery['node']>
// The `node` union is either the resolved UserContentEdit shape or an empty object
// (`Record<PropertyKey, never>`, for an id that resolves to a different node type).
// Only the empty variant is assignable to that record type, so Exclude drops it.
export type EditHistoryNode = Exclude<DialogNode, Record<PropertyKey, never>>

export function editHistoryDialogQueryKey(userContentEditId: string) {
  return ['markdown-edit-history-viewer', 'edit-history-dialog', userContentEditId] as const
}

// A resolved UserContentEdit always carries `editedAt`; the other `node` union variant
// is an empty object (an id that resolved to a different node type). Its index signature
// means `'editedAt' in node` alone won't exclude it, so narrow via a type predicate.
function isEditHistoryNode(node: DialogNode | null | undefined): node is EditHistoryNode {
  return !!node && 'editedAt' in node
}

export async function fetchEditHistoryDialog(
  userContentEditId: string,
  signal?: AbortSignal,
): Promise<EditHistoryNode> {
  const response = await verifiedFetchJSON(`/issues/edit-history-dialog?id=${encodeURIComponent(userContentEditId)}`, {
    signal,
  })
  if (!response.ok) throw new Error(`Edit history dialog fetch failed (${response.status})`)

  const json = (await response.json()) as {data?: EditHistoryDialogQueryQuery; errors?: unknown[]}
  if (json.errors?.length || !json.data) throw new Error('Edit history dialog query returned errors')

  const node = json.data.node
  if (!isEditHistoryNode(node)) throw new Error('Edit history dialog query returned no edit')

  return node
}
